/**
 * ScrollToTop.tsx
 *
 * File-level: Small utility component that resets window scroll to the top
 * whenever the router location changes. Use this in the app root (inside a Router)
 * to ensure each navigation starts at the top of the page.
 */

import React, { useEffect } from 'react'
import { useLocation } from 'react-router'

/**
 * ScrollToTop component
 *
 * Component-level: Listens for route/location changes and scrolls the window
 * to the top. Keeps behavior minimal and reliable on client-side navigation.
 *
 * Notes:
 * - Uses immediate scroll to avoid jumpy behavior in some browsers. If you prefer
 *   smooth scrolling, change the scrollOptions behavior to 'smooth'.
 */
export default function ScrollToTop(): null {
  const location = useLocation()

  /**
   * Effect-level: Scroll to top whenever location changes.
   */
  useEffect(() => {
    try {
      // Use immediate jump to top for predictable behavior after navigation.
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
      // If you prefer smooth behavior, use:
      // window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
    } catch {
      // Silently ignore in non-browser environments
    }
    // Depend on pathname and hash to be precise about different navigations
  }, [location.pathname, location.hash])

  return null
}